import {
    format,
    ISO_DATE_ONLY_FORMAT,
    zodDateOfBirthValidation,
    zodFirstNameValidation,
    zodLastNameValidation,
} from '@fruitful/fruitils';
import { AnalyticsUserEvent, isSubscriptionProductId } from '@fruitful/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { Redirect, router, useLocalSearchParams } from 'expo-router';
import { useSetAtom } from 'jotai';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    ControlledDateTextInput,
    ControlledInput,
    ControlledPhoneNumberInput,
    FrostedSurface,
    isNonMobileWeb,
    Spinner,
    View,
    XStack,
    YStack,
} from 'smoothie';
import z from 'zod';

import { NewMemberInfoDisclosure } from '../../../banking/components/Disclosures';
import { IS_PRODUCTION } from '../../../config/FruitfulEnvironment';
import { useViewEvents } from '../../../events/hooks/useViewEvents';
import { track, trackGrowthMarketingEvent } from '../../../logger/Logger';
import { useUser } from '../../../user/providers/UserProvider';
import { useVisitorId } from '../../../user/providers/VisitorProvider';
import type { User } from '../../../user/types/User';
import { useCreateMemberWithSubscription } from '../../hooks/start-membership/useCreateMemberWithSubscription';
import { useStytchSession, useStytchUser } from '../../hooks/useAuth';
import { isUserSetupInProgressAtom } from '../../state/AuthAtoms';
import { continueToNextPage } from '../../utils/continueToNextPage';
import { registerDeviceToken } from '../../utils/registerDeviceToken';
import { NewMemberScreenLayout } from './NewMemberScreenLayout';

const newMemberInfoSchema = z.object({
    firstName: zodFirstNameValidation,
    lastName: zodLastNameValidation,
    dateOfBirth: zodDateOfBirthValidation,
    phoneNumber: z.string().length(10, 'Please enter a valid phone number.'),
});
type NewMemberInfoSchema = z.infer<typeof newMemberInfoSchema>;

export const NewMemberInfo = () => {
    const { trackViewOnce } = useViewEvents();
    trackViewOnce('SignUp/NewMemberInfo');

    const { user } = useUser();
    const [submitError, setSubmitError] = useState<Error | null>(null);
    // Spans the whole submit -> navigate window, not just the
    // createMemberWithSubscription network call: registerDeviceToken (in
    // onCreateMemberSuccess) runs after that call resolves and isn't itself a
    // tracked mutation, so relying on the mutation's own pending flag alone
    // caused inputs/button to flash re-enabled between the request finishing
    // and the actual navigation.
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { user: stytchUser } = useStytchUser();
    const { referralCode, subscriptionProductId } = useLocalSearchParams<{
        referralCode?: string;
        subscriptionProductId?: string;
    }>();
    const overrideSubscriptionProductId =
        !IS_PRODUCTION && isSubscriptionProductId(subscriptionProductId)
            ? subscriptionProductId
            : undefined;
    const visitorId = useVisitorId();
    const { session: stytchSession } = useStytchSession();
    const setIsUserSetupInProgress = useSetAtom(isUserSetupInProgressAtom);

    const form = useForm<NewMemberInfoSchema>({
        resolver: zodResolver(newMemberInfoSchema),
        defaultValues: {
            firstName: '',
            lastName: '',
            dateOfBirth: '',
            phoneNumber: '',
        },
    });

    const { createMemberWithSubscription } = useCreateMemberWithSubscription();

    const onCreateMemberSuccess = useCallback(
        async (user: User) => {
            try {
                if (stytchSession != null && stytchUser?.user_id != null) {
                    await registerDeviceToken(
                        stytchUser.user_id,
                        stytchSession.session_id,
                    );
                }
                // Matches StartMembership.tsx: flip this off once the device
                // token is stored so UserProvider re-evaluates the user as
                // fully authed before we navigate into the authenticated app.
                setIsUserSetupInProgress(false);
                continueToNextPage();
                track(AnalyticsUserEvent.SIGNUP_FUNNEL_NEW_MEMBER_INFO_SUBMIT);
                trackGrowthMarketingEvent(
                    AnalyticsUserEvent.MEMBERSHIP_PURCHASE_PUBLISHED,
                    {
                        membershipPlanAmount: 0,
                        membershipEmail: user.email,
                        stripeCustomerId: user.stripeCustomerId ?? '',
                    },
                );
            } catch (error) {
                setIsUserSetupInProgress(false);
                setIsSubmitting(false);
                setSubmitError(
                    error instanceof Error ? error : new Error(String(error)),
                );
            }
        },
        [stytchUser, stytchSession, setIsUserSetupInProgress],
    );

    const onCreateMemberError = useCallback(
        (error: Error) => {
            setIsUserSetupInProgress(false);
            setIsSubmitting(false);
            setSubmitError(error);
        },
        [setIsUserSetupInProgress],
    );

    const onFormSubmit = useCallback(
        (formData: NewMemberInfoSchema) => {
            const email = stytchUser?.emails?.[0]?.email;
            if (stytchUser?.user_id == null || email == null) {
                router.push({
                    pathname: '/sign-up/new-member',
                    params: {
                        ...(referralCode != null ? { referralCode } : {}),
                        ...(overrideSubscriptionProductId != null
                            ? {
                                  subscriptionProductId:
                                      overrideSubscriptionProductId,
                              }
                            : {}),
                    },
                });
                return;
            }
            setSubmitError(null);
            setIsSubmitting(true);
            // Suppresses UserProvider's fetch until the new member is fully
            // set up (device token stored) — otherwise it caches a
            // not-fully-authed user and the (app) auth guard bounces us to
            // sign-in as soon as we navigate past this screen.
            setIsUserSetupInProgress(true);
            createMemberWithSubscription({
                email,
                stytchId: stytchUser.user_id,
                firstName: formData.firstName,
                lastName: formData.lastName,
                phoneNumber: `+1${formData.phoneNumber}`,
                dateOfBirth: `${format(formData.dateOfBirth, ISO_DATE_ONLY_FORMAT)}T00:00:00Z`,
                ...(referralCode != null ? { referralCode } : {}),
                visitorId,
                ...(overrideSubscriptionProductId != null
                    ? {
                          subscriptionProductId: overrideSubscriptionProductId,
                      }
                    : {}),
                onSuccess: onCreateMemberSuccess,
                onError: onCreateMemberError,
            });
        },
        [
            stytchUser,
            referralCode,
            overrideSubscriptionProductId,
            visitorId,
            createMemberWithSubscription,
            onCreateMemberSuccess,
            onCreateMemberError,
            setIsUserSetupInProgress,
        ],
    );

    const hasSubmitError =
        submitError?.message != null && submitError.message.length > 0;

    if (user?.isFullyAuthed) {
        return <Redirect href="/home" />;
    }

    return (
        <NewMemberScreenLayout title="A few more details">
            <FrostedSurface padding={24} borderRadius={20}>
                <XStack gap={12}>
                    <YStack flex={1}>
                        <ControlledInput
                            name="firstName"
                            label="First name"
                            placeholder="First name"
                            autoCapitalize="words"
                            autoComplete="name-given"
                            textContentType="givenName"
                            control={form.control}
                            register={form.register}
                            errors={form.formState.errors}
                            onSubmitEditing={() => {
                                form.setFocus('lastName');
                            }}
                            // eslint-disable-next-line jsx-a11y/no-autofocus
                            autoFocus={isNonMobileWeb}
                            disabled={isSubmitting}
                        />
                    </YStack>
                    <YStack flex={1}>
                        <ControlledInput
                            name="lastName"
                            label="Last name"
                            placeholder="Last name"
                            autoCapitalize="words"
                            autoComplete="name-family"
                            textContentType="familyName"
                            control={form.control}
                            register={form.register}
                            errors={form.formState.errors}
                            onSubmitEditing={() => {
                                form.setFocus('dateOfBirth');
                            }}
                            disabled={isSubmitting}
                        />
                    </YStack>
                </XStack>

                <View marginTop={16}>
                    <ControlledDateTextInput
                        name="dateOfBirth"
                        label="Date of birth"
                        control={form.control}
                        register={form.register}
                        errors={form.formState.errors}
                        onSubmitEditing={() => {
                            form.setFocus('phoneNumber');
                        }}
                        disabled={isSubmitting}
                    />
                </View>

                <View marginTop={16}>
                    <ControlledPhoneNumberInput
                        name="phoneNumber"
                        label="Mobile number"
                        autoComplete="tel-national"
                        inputMode="tel"
                        keyboardType="numbers-and-punctuation"
                        textContentType="telephoneNumber"
                        control={form.control}
                        register={form.register}
                        errors={form.formState.errors}
                        onSubmitEditing={form.handleSubmit(onFormSubmit)}
                        disabled={isSubmitting}
                    />
                </View>

                <View
                    animation="quick"
                    opacity={hasSubmitError ? 1 : 0}
                    height={hasSubmitError ? 48 : 0}
                    marginTop={hasSubmitError ? 16 : 0}
                    overflow="hidden"
                    animateOnly={['opacity', 'height', 'marginTop']}
                >
                    <BodySmallRegular color="$red1100">
                        {submitError?.message ?? ''}
                    </BodySmallRegular>
                </View>

                <View marginTop={16}>
                    <NewMemberInfoDisclosure />
                </View>

                <View marginTop={16}>
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.LARGE}
                        onPress={form.handleSubmit(onFormSubmit)}
                        disabled={isSubmitting || hasSubmitError}
                        text={isSubmitting ? '' : 'Submit'}
                        icon={isSubmitting ? <Spinner /> : null}
                    />
                </View>
            </FrostedSurface>
        </NewMemberScreenLayout>
    );
};
